/* eslint-disable camelcase */
import { getCurrency, getOptions } from "@companion-core/shared/app/Utils/config";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { getSummaryValue } from "@companion-core/shared/app/Components/Inputs/JPInputCounters/JPInputCounters.model";
import { APIRequest } from "@companion-core/shared/app/Interfaces/API";
import {
  accessibleRequestFields,
  serviceIcons,
  subFamilyIcons,
} from "@companion-core/shared/app/Constants/myRequests";
import { Field } from "@companion-core/shared/app/Interfaces/wizard";
import { slugifyText } from "@companion-core/shared/app/Utils/format";
import { isEqualLocation } from "@companion-core/shared/app/Components/Requests/RequestCard/requestCard.model";
import * as countries from "i18n-iso-countries";
import { TFunction } from "i18next";
dayjs.extend(localizedFormat);
dayjs.extend(utc);

export const getRequestSubject = (
  t: TFunction,
  exists: (key: string) => boolean,
  family: string | null | undefined,
  subFamily: string | null | undefined
): string => {
  if (family) {
    const subFamilyKey = `request.my-requests.subject.${family}.${subFamily ?? "default"}`;
    if (exists(subFamilyKey)) return t(subFamilyKey);
    const familyKey = `request.my-requests.subject.${family}.default`;
    if (exists(familyKey)) return t(familyKey);
  }
  const genericKey = "request.my-requests.subject.default";
  if (exists(genericKey)) return t(genericKey);
  return t("request.concierge.form.subject.default");
};

export const getFamilyIcon = (family: string, subfamily?: string | null): string => {
  if (subfamily && subFamilyIcons[subfamily]) return subFamilyIcons[subfamily];
  if (serviceIcons[family]) return serviceIcons[family];
  return "JP-Concierge-bell";
};

export const createSortedSummaryFields = (
  data: APIRequest,
  t: TFunction,
  language: string
): Field[] => {
  const summary: Field[] = [];
  const summaryFields: Record<string, unknown> = {};
  const includeStartOrEndLocation =
    Object.keys(data).includes("start_location") || Object.keys(data).includes("end_location");

  // Create an object with sorted fields
  Object.keys(accessibleRequestFields).forEach((key) => (summaryFields[key] = null));

  // Fill your pre-sorted Object
  Object.entries(data).map(([key, value]): void => {
    const isDateField = ["start_date", "end_date"].includes(key);
    const displayTimeInSummary = getDisplayTimeInSummary(
      data.family ?? "",
      data.sub_family ?? undefined
    );
    if (key === "location" && includeStartOrEndLocation) return; // skip location if start or end location is present
    if (
      key === "end_location" &&
      (isEqualLocation(data.start_location, data.end_location) || !value.city)
    )
      return; // skip end_location if it is equal to start_location or if the city is null
    if (accessibleRequestFields[key] && value) {
      summaryFields[key] = {
        name: key,
        label: `request.my-requests.summary.${(() => {
          if (
            key === "start_location" &&
            (isEqualLocation(data.start_location, data.end_location) || !data.end_location.city)
          ) {
            return "location";
          } else if (key === "start_date" && !data["end_date"]) {
            return "date";
          } else {
            return key;
          }
        })()}.label`,
        icon: accessibleRequestFields[key],
        required: true,
        testID: `request-detail__summary-field-${key}`,
        type: "text",
        summaryValue: formatRequestSummaryField(
          key,
          value,
          t,
          language,
          data.family ?? "",
          data.sub_family ?? undefined
        ),
        formattedSummaryDate: isDateField
          ? getFormattedSummaryDate(value as string, displayTimeInSummary, t)
          : undefined,
        value: isDateField ? getSummaryDateTimeValue(value as string, displayTimeInSummary) : value,
      };
    }
  });

  // Convert your sorted object to an array for the Summary component
  Object.values(summaryFields).forEach((value) => {
    if (value) summary.push(value as Field);
  });

  return summary;
};

const getSummaryDateTimeValue = (
  date: string,
  displayTimeInSummary: boolean
): string | undefined => {
  const dateFormat = displayTimeInSummary ? "YYYY-MM-DDTHH:mm" : "YYYY-MM-DD";
  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) return undefined;
  return parsedDate.utc().format(dateFormat);
};

export const getFormattedSummaryDate = (
  date: string,
  displayTimeInSummary: boolean,
  t: TFunction
): string => {
  if (!dayjs(date).isValid()) return "";
  if (displayTimeInSummary)
    return dayjs(date)
      .utc()
      .format(`LL [${t("request.my-requests.time.particle")}] LT`);
  return dayjs(date).utc().format("LL");
};

export const getDisplayTimeInSummary = (family: string, subfamily: string | undefined): boolean => {
  const { familyWithHourDisplayed } = getOptions();
  if (
    Object.keys(familyWithHourDisplayed).includes(family) &&
    (!familyWithHourDisplayed[family] ||
      (familyWithHourDisplayed[family] &&
        subfamily &&
        familyWithHourDisplayed[family].includes(subfamily)))
  ) {
    return true;
  }
  return false;
};

export const formatRequestSummaryField = (
  name: string,
  value: string | number | Record<string, unknown>,
  t: TFunction,
  language: string,
  family: string,
  subfamily: string | undefined
) => {
  const formatLocation = (location: Record<string, unknown>): string => {
    const city = location.city && !location.city.toString().includes("null") ? location.city : "";
    const zipCode = location["zip_code"] ? `${location["zip_code"]} ` : "";
    const countryName = countries.getName(location?.country_code?.toString() ?? "", language, {
      select: "official",
    });
    let country = countryName ? `, ${countryName}` : "";
    const street =
      location.street &&
      !location.street.toString().includes("null") &&
      !location.street.toString().includes(`${city}${country}`)
        ? `${location.street}, `
        : "";
    const comma = city || zipCode || street ? ", " : "";
    const name = location.name ? `${location.name}${comma} ` : "";
    if (country && !(city || zipCode || street || name)) {
      // The street depends on the city + country, so we can't do this check at the start.
      country = countryName ?? "";
    }
    return `${name}${street}${zipCode}${city}${country}`;
  };

  // TODO: Change this once date and time fields are splitted
  const formatDateTime = (date: string): string => {
    let format = "L";
    if (getDisplayTimeInSummary(family, subfamily)) {
      format = `L [${t("request.my-requests.time.particle")}] LT`;
    }
    return dayjs(date).utc().format(format);
  };

  const formattedData: Record<string, string> = {
    "additional_information_digital": value as string,
    "location": formatLocation(value as Record<string, string>),
    "start_location": formatLocation(value as Record<string, string>),
    "end_location": formatLocation(value as Record<string, string>),
    "start_date": formatDateTime(value as string),
    "end_date": formatDateTime(value as string),
    "participants": getSummaryValue(value as Record<string, number>, t),
    "max_budget": getCurrency().template.replace("{val}", value as number),
    "description": value as string,
    "number_of_rooms": value as string,
    "category_class": t(`request.concierge.form.car-rental.category.${value}.label`),
    "start_time": value as string,
    "end_time": value as string,
  };
  return formattedData[name];
};

export const slugifyRequest = (request?: APIRequest): APIRequest | null => {
  if (!request) return null;
  const { family, sub_family, status, category_class } = request;
  return {
    ...request,
    family: family ? slugifyText(family) : null,
    sub_family: sub_family ? slugifyText(sub_family) : null,
    status: status ? slugifyText(status) : status,
    category_class: category_class ? slugifyText(category_class) : null,
  };
};
